import { useState } from 'react';
import { FaFileExcel, FaSpinner } from 'react-icons/fa';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import apiRelatorio from '../../services/apis/apiRelatorio';
import useToast from '../../hooks/useToast';
import { getErrorMessage } from '../../utils/errorMessage';

const DIAS_MAXIMO = 366;

const hojeIso = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' });
const inicioDoMesIso = () => `${hojeIso().slice(0, 8)}01`;
const diasEntre = (inicio, fim) => Math.round((new Date(`${fim}T00:00:00`) - new Date(`${inicio}T00:00:00`)) / 86_400_000);
const formatarData = (iso) => iso.split('-').reverse().join('-');

const classeCampo = 'bg-gray-800 text-white border border-gray-700 rounded px-3 py-2 focus:outline-none focus:border-accent disabled:opacity-50';

// Com responseType 'blob', o corpo do erro também chega como Blob e precisa ser lido como JSON
const lerErroBlob = async (error) => {
    const data = error?.response?.data;
    if (!(data instanceof Blob)) return error;
    try {
        return { response: { ...error.response, data: JSON.parse(await data.text()) } };
    } catch {
        return error;
    }
};

export default function RelatorioManutencao() {
    const { erro } = useToast();
    const [aberto, setAberto] = useState(false);
    const [enviando, setEnviando] = useState(false);
    const [dataInicio, setDataInicio] = useState(inicioDoMesIso);
    const [dataFim, setDataFim] = useState(hojeIso);

    const validar = () => {
        if (!dataInicio || !dataFim) return 'Informe a data inicial e a data final.';
        if (dataFim < dataInicio) return 'A data final precisa ser igual ou posterior à data inicial.';
        if (diasEntre(dataInicio, dataFim) >= DIAS_MAXIMO) return `O período do relatório pode ter no máximo ${DIAS_MAXIMO} dias.`;
        return null;
    };

    const gerar = async (e) => {
        e.preventDefault();
        if (enviando) return;

        const mensagem = validar();
        if (mensagem) {
            erro(mensagem);
            return;
        }

        setEnviando(true);
        try {
            const response = await apiRelatorio.relatorioManutencao({ dataInicio, dataFim });
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Manutencao_de_${formatarData(dataInicio)}_a_${formatarData(dataFim)}.xlsx`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            setAberto(false);
        } catch (error) {
            erro(getErrorMessage(await lerErroBlob(error), 'Erro ao gerar relatório de manutenção'));
        } finally {
            setEnviando(false);
        }
    };

    return (
        <>
            <button
                type="button"
                onClick={() => setAberto(true)}
                disabled={enviando}
                title="Exportar Excel das atividades realizadas"
                className="flex items-center gap-2 bg-gray-800 text-white border border-gray-700 px-3 py-1.5 rounded-md text-lg disabled:opacity-50"
            >
                {enviando ? <FaSpinner className="animate-spin" /> : <FaFileExcel />}
                Exportar Excel
            </button>
            <Dialog open={aberto} onOpenChange={(v) => !v && !enviando && setAberto(false)}>
                <DialogContent>
                    <form onSubmit={gerar} className="flex flex-col gap-4">
                        <DialogHeader>
                            <DialogTitle>Relatório de atividades realizadas</DialogTitle>
                        </DialogHeader>
                        <div className="flex flex-col sm:flex-row gap-3">
                            <label className="flex flex-col gap-1 text-lg flex-1">
                                De
                                <input
                                    type="date"
                                    required
                                    value={dataInicio}
                                    max={hojeIso()}
                                    disabled={enviando}
                                    onChange={(e) => setDataInicio(e.target.value)}
                                    className={classeCampo}
                                />
                            </label>
                            <label className="flex flex-col gap-1 text-lg flex-1">
                                Até
                                <input
                                    type="date"
                                    required
                                    value={dataFim}
                                    max={hojeIso()}
                                    disabled={enviando}
                                    onChange={(e) => setDataFim(e.target.value)}
                                    className={classeCampo}
                                />
                            </label>
                        </div>
                        <DialogFooter>
                            <button
                                type="submit"
                                disabled={enviando}
                                className="flex items-center gap-2 bg-accent text-white px-4 py-2 rounded-md text-lg disabled:opacity-50"
                            >
                                {enviando && <FaSpinner className="animate-spin" />}
                                Gerar relatório
                            </button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </>
    );
}
